/** @file Commands for a hierarchy drag-and-drop: reparent or reorder a bone, move a slot; null = refused. */
import type { CommandCall, Doc, DocIndex, Id } from '@pose2d/format/types';
import type { DropWhere } from '../../ui-kit/logic/dropPosition';
import { parseRowId, type HierarchyRow } from './parseRowId';

/** A user bone that may move: not the root, not a generated socket bone. */
function movable(doc: Doc, id: Id): boolean {
  const bone = doc.bones[id];
  return bone !== undefined && bone.parent !== null && bone.kind === undefined;
}

/** True when `bone` is `ancestor` or lies below it. */
function within(doc: Doc, bone: Id, ancestor: Id): boolean {
  for (let id: Id | null = bone; id !== null; id = doc.bones[id]?.parent ?? null) if (id === ancestor) return true;
  return false;
}

/** A parent the bone may go under: a user bone that is neither the bone nor one of its descendants. */
const parentOk = (doc: Doc, bone: Id, parent: Id): boolean => doc.bones[parent]?.kind === undefined && !within(doc, parent, bone);

/** Drop onto a bone row: bone.setParent keeping the world setup pose. */
function into(doc: Doc, bone: Id, parent: Id): CommandCall[] | null {
  if (!movable(doc, bone) || !parentOk(doc, bone, parent) || doc.bones[bone]?.parent === parent) return null;
  return [{ name: 'bone.setParent', input: { target: bone, parent, keepWorld: true } }];
}

/** The parent `bone` would share with `sibling`, or null when that move is not allowed. */
function besideParent(doc: Doc, bone: Id, sibling: Id): Id | null {
  const parent = doc.bones[sibling]?.parent ?? null;
  return parent !== null && bone !== sibling && movable(doc, bone) && parentOk(doc, bone, parent) ? parent : null;
}

/** `current` children with `bone` moved next to `sibling` (before it, or after it when `after`). */
function siblingOrder(current: readonly Id[], bone: Id, sibling: Id, after: boolean): Id[] {
  const order = current.filter((id) => id !== bone);
  order.splice(order.indexOf(sibling) + (after ? 1 : 0), 0, bone);
  return order;
}

/** Drop before/after a sibling: setParent when the parent changes, then the parent's full child order. */
function beside(doc: Doc, index: DocIndex, bone: Id, sibling: Id, after: boolean): CommandCall[] | null {
  const parent = besideParent(doc, bone, sibling);
  if (parent === null) return null;
  const current = index.children.get(parent) ?? [];
  const order = siblingOrder(current, bone, sibling, after);
  const moved = doc.bones[bone]?.parent !== parent;
  if (!moved && order.every((id, i) => id === current[i])) return null;
  const reparent: CommandCall[] = moved ? [{ name: 'bone.setParent', input: { target: bone, parent, keepWorld: true } }] : [];
  return [...reparent, { name: 'bone.reorderChildren', input: { parent, order } }];
}

/** The bone a slot lands on when dropped on `to` (a bone row, or the bone of a slot/placeholder row). */
function landingBone(doc: Doc, to: HierarchyRow): Id | undefined {
  if (to.kind === 'bone') return to.id;
  return doc.slots[to.kind === 'slot' ? to.id : to.slot]?.bone;
}

/** Commands for dropping row `drag` at `where` of row `target`; null when the drop is refused or changes nothing. */
export function dropCalls(doc: Doc, index: DocIndex, drag: string, target: string, where: DropWhere): CommandCall[] | null {
  const from = parseRowId(drag);
  const to = parseRowId(target);
  if (from.kind === 'slot') {
    const bone = landingBone(doc, to);
    return bone === undefined || bone === doc.slots[from.id]?.bone ? null : [{ name: 'slot.setBone', input: { target: from.id, bone } }];
  }
  if (from.kind !== 'bone' || to.kind !== 'bone') return null;
  return where === 'into' ? into(doc, from.id, to.id) : beside(doc, index, from.id, to.id, where === 'after');
}
